import { Observable, of, from, timer, iif } from 'rxjs';
import { catchError, switchMap, map, mergeMap, takeUntil } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import LoginService from '../services/login.service';
import AuthUtils from '../../shared/utils/auth-utils';
import {
  LoginRequest,
  LoginSuccess,
  LoginError,
  LoginAction,
  LoginActions,
  Logout,
  LogoutSuccess,
  LogoutFailed, WatchExpiration, StopWatchExpiration, ContinueWatchExpiration, SessionExpired,
} from '../actions';
import { LoadUserProfileInfo, ResetUserState } from '../../profile/actions/profile.action';
import { CreateErrorNotice } from '../../store/actions/toaster.action';
import { SendGTMEvent } from '../../store/actions/tagManager.action';
import { RootState } from '../../store/state';
import { DisconnectChat } from '../../chat/actions/chat.action';

const EXPIRATION_POLL_TIME = 24 * 60 * 60 * 1000; // Check once a day


const loginEpic = (action$: Observable<LoginAction>) => action$.pipe(
  ofType<LoginRequest>(LoginActions.LoginRequest),
  switchMap((action) => from(LoginService.login(action.apiOptions)).pipe(
    map(() =>
      new LoginSuccess({
        authed: true,
        sessionToken: AuthUtils.getAccessToken(),
        sessionTokenExpiration: new Date(AuthUtils.getAccessTokenExp()),
        errorText: '',
      }),
    ),
    catchError(() => (
      of(
        new LoginError({
          authed: false,
          sessionToken: '',
          sessionTokenExpiration: null,
          errorText: 'showErrorHTML',
        })
      )
    )),
  )),
);

const loginSuccessEpic = (action$: Observable<LoginAction>) => action$.pipe(
  ofType<LoginSuccess>(LoginActions.LoginSuccess),
  mergeMap(() => of(
    new WatchExpiration(),
    new LoadUserProfileInfo({
      event: 'login',
      isLoadAfterLogin: true
    }),
  )),
);

const logoutEpic = (action$: Observable<LoginAction>,state$: StateObservable<RootState>) => action$.pipe(
  ofType<Logout>(LoginActions.Logout),
  switchMap(() => from(LoginService.logout()).pipe(
    mergeMap(() => of(
      new StopWatchExpiration(),
      new DisconnectChat(),
      new SendGTMEvent({
        event: 'logout',
        org_id: state$?.value?.profile?.userInfo?.orgId,
        user_id: state$?.value?.profile?.userInfo?.id,
        roles: state$?.value?.profile?.userInfo?.roles
      }),
      new LogoutSuccess(),
    )),
    catchError((error) => (
      of(new LogoutFailed(error))
    )),
  )),
);

const logoutSuccessEpic = (action$: Observable<LoginAction>) => action$.pipe(
  ofType<LogoutSuccess>(LoginActions.LogoutSuccess),
  map(() => new ResetUserState()),
);

const loginExpirationWatcherEpic = (action$: Observable<LoginAction>) => action$.pipe(
  ofType<WatchExpiration>(LoginActions.WatchExpiration),
  mergeMap(() => {
    return timer(0, EXPIRATION_POLL_TIME).pipe(
      mergeMap(() => iif(
        () => LoginService.hasExpired(),
        of(new SessionExpired()),
        of(new ContinueWatchExpiration()),
      )),
      takeUntil(action$.pipe(
        ofType(LoginActions.StopWatchExpiration)
      )),
    );
  }),
);

const loginSessionExpired = (action$: Observable<LoginAction>) => action$.pipe(
  ofType<SessionExpired>(LoginActions.SessionExpired),
  switchMap(() => from(LoginService.markExpired()).pipe(
    map(() => new Logout()),
    catchError(() => of(
      new CreateErrorNotice('Error', 'Failed to stop login expiration watch.'),
    )),
  )),
);

const LoginEpics = [
  loginEpic,
  loginSuccessEpic,
  logoutEpic,
  logoutSuccessEpic,
  loginExpirationWatcherEpic,
  loginSessionExpired,
];

export default LoginEpics;
